import {
  cn,
  convertUtcToLocal12Hour,
  formatFullDate,
  formatTime,
} from "../../../lib/utils";
import { Spinner } from "../../../components/Spinner";
import { useGetScans } from "../../../helpers/queries/scans/useGetScans";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  deleteScanWithId,
  exportAsCsv,
  exportAsPdf,
  exportSetup,
  repeatScanWithId,
  startScanWithId,
  stopScanWithId,
} from "../../../helpers/api";
import { useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { FiCompass, FiPieChart, FiTrash2 } from "react-icons/fi";
import { FaCircleCheck, FaRegCircleStop, FaFileZipper } from "react-icons/fa6";
import { BsArrowRepeat } from "react-icons/bs";
import { MdPending } from "react-icons/md";
import { IoIosCloseCircle } from "react-icons/io";
import { LuDownload } from "react-icons/lu";
import CircularScore from "../../../components/CircularScore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import csv from "../../../assets/icons/csv.png";
import pdf from "../../../assets/icons/pdf.png";
import empty from "../../../assets/images/empty_scan.png";
import ScanAssetModal from "../../../components/modals/ScanAssetModal";
// import { useGetAllTargets } from "../../../helpers/queries/targets/useGetTargets";
import AddTargetModal from "../Targets/modals/AddTargetModal";
import { usePaginationSync } from "../../../hooks/usePaginationSync";
import { PaginationComponent } from "../../../components/Pagination";
import ScanActionsModal from "../../../components/modals/ScanActionsModal";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import UpdateScheduleScanModal from "../../../components/modals/UpdateScheduleScanModal";
import RunningStatus from "../../../components/RunningStatus";
import { useGetUserRole } from "../../../hooks/useGetUserRole";
import { useGetAssetGroups } from "../../../helpers/queries/scans/useGetInventory";

const ScanHistory = () => {
  const [status, setStatus] = useState("");
  const [targetName, setTargetName] = useState("");
  const [schedule, setSchedule] = useState(false);

  const [lastPage, setLastPage] = useState(1);
  const { currentPage, updatePage } = usePaginationSync(lastPage);
  const { isLoading, isSuccess, isError, data } = useGetScans(
    currentPage,
    status,
    schedule
  );
  // const { data: targets } = useGetAllTargets();
  const { data: assetGroupList } = useGetAssetGroups(0);

  const { viewer } = useGetUserRole();
  useEffect(() => {
    setLastPage(data?.data?.last_page);
  }, [data]);

  const [showExtra, setShowExtra] = useState(null);

  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { key: "all", label: "All" },
    { key: "scheduled", label: "Scheduled" },
  ];

  useEffect(() => {
    if (activeTab === "scheduled") {
      setSchedule(true);
    } else {
      setSchedule(false);
    }
    if (status === "all") {
      setStatus("");
    }
  }, [activeTab, status]);

  const [openDeleteModal, setOpenDeleteModal] = useState(null);
  const handleDeleteModal = () => setOpenDeleteModal(null);

  const [openRunScanModal, setOpenRunScanModal] = useState(null);
  const handleRunScanModal = () => setOpenRunScanModal(null);

  const [openStopScanModal, setOpenStopScanModal] = useState(null);
  const handleStopScanModal = () => setOpenStopScanModal(null);

  // Check for URL parameters
  const { id } = useParams(); // Get the encoded ID from the URL
  const decodedId = id ? atob(id) : null; // Decode the ID
  // Convert decodedId to number for proper comparison with item.target.id
  const numericDecodedId = decodedId ? parseInt(decodedId, 10) : null;

  const [scanId, setScanId] = useState(null);
  const [activeModalId, setActiveModalId] = useState(null);

  const [fetchPdfLoading, setFetchPdfLoading] = useState(false);

  const fetchPdf = async (id) => {
    setFetchPdfLoading(true);
    try {
      const file = await exportAsPdf(id);

      if (!(file instanceof Blob)) {
        console.error("Error: Received invalid PDF file", file);
        toast.error("Failed to download");
        return;
      }
      // console.log("PDF Blob:", file);
      const pdfUrl = window.URL.createObjectURL(file);

      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `${targetName} report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to fetch PDF:", error);
      toast.error("Failed to download");
    } finally {
      setFetchPdfLoading(false);
      setActiveModalId(null); // Close modal
    }
  };

  const [downloadCsvLoading, setDownloadCsvLoading] = useState(false);

  const downloadCsv = async (id) => {
    setDownloadCsvLoading(true);
    try {
      const file = await exportAsCsv(id);

      if (!(file instanceof Blob)) {
        console.error("Error: Received invalid CSV file", file);
        toast.error("Failed to download");
        return;
      }
      const url = window.URL.createObjectURL(file);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${targetName} report.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to fetch CSV:", error);
      toast.error("Failed to download");
    } finally {
      setDownloadCsvLoading(false);
      setActiveModalId(null); // Close modal
    }
  };

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (id) => startScanWithId(id),
    onSuccess: (data) => {
      toast(data.message);
      queryClient.invalidateQueries({ queryKey: ["scans"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to start scan");
    },
  });

  const startScan = (id) => {
    setScanId(id);
    mutate(id);
  };

  const { mutate: repeat, isPending: repeatScanPending } = useMutation({
    mutationFn: (id) => repeatScanWithId(id),
    onSuccess: (data) => {
      toast(data.message);
      handleRunScanModal();
      queryClient.invalidateQueries({ queryKey: ["scans"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to repeat scan");
    },
  });

  const repeatScan = (id) => {
    repeat(id);
  };

  const { mutate: stop, isPending: stopScanPending } = useMutation({
    mutationFn: (id) => stopScanWithId(id),
    onSuccess: (data) => {
      toast(data.message);
      handleStopScanModal();
      queryClient.invalidateQueries({ queryKey: ["scans"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to stop scan");
    },
  });

  const stopScan = (id) => {
    stop(id);
  };

  const { mutate: deleteMutation, isPending: deleteScanPending } = useMutation({
    mutationFn: (id) => deleteScanWithId(id),
    onSuccess: (data) => {
      toast(data.message);
      handleDeleteModal();
      queryClient.invalidateQueries({ queryKey: ["scans"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete scan");
    },
  });

  const deleteScan = (id) => {
    deleteMutation(id);
  };

  const [loadingExport, setLoadingExport] = useState(false);

  const handleDownloadZip = async () => {
    setLoadingExport(true);
    try {
      const blob = await exportSetup();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "setup.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download ZIP", error);
      toast.error("Download failed");
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      <div className="w-full">
        <div className=" w-full lg:block flex justify-between items-center py-6 lg:px-10 md:px-5 px-4 bg-white border-b mb-4">
          <div className="w-full flex lg:flex-row flex-col justify-between lg:items-center gap-4 mb-1">
            <div className="flex gap-3 items-center">
              <div>
                <div className="w-[50px] h-[50px] rounded-full border border-neutral300 flex justify-center items-center">
                  <FiCompass className="text-neutral700 text-[20px]" />
                </div>
              </div>
              <div>
                <p className="text-darkGrey font-semibold md:block hidden">
                  Scans
                </p>
                <p className="text-neutral600 md:text-base text-sm">
                  Run scan and manage your history to uncover potential
                  vulnerabilities.
                </p>
              </div>
            </div>
            {!viewer &&
              (assetGroupList && assetGroupList?.data?.length === 0 ? (
                <AddTargetModal
                  is_asset_group={true}
                  triggerLabel="Create asset group"
                />
              ) : (
                <div className="flex flex-row md:gap-4 gap-2 md:items-center">
                  <Button
                    className="bg-neutral200 text-neutral800 px-3"
                    variant="outline"
                    onClick={handleDownloadZip}
                    loading={loadingExport}
                  >
                    <FaFileZipper />
                    Export setup
                  </Button>
                  <ScanAssetModal />
                </div>
              ))}
          </div>
        </div>

        <div className="flex md:flex-row flex-col gap-4 justify-between md:items-center lg:px-10 md:px-5 px-4">
          <div className="w-[250px] rounded-lg bg-neutral200 h-[40px] flex py-1 relative overflow-hidden">
            <div
              className={`absolute top-1 left-1  h-[calc(100%-8px)] rounded-lg bg-white transition-all duration-500`}
              style={{
                width: `${97.5 / tabs.length}%`,
                transform: `translateX(${
                  tabs.findIndex((tab) => tab.key === activeTab) * 100
                }%)`,
              }}
            ></div>

            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`flex-1 relative z-10 text-center font-medium transition-colors duration-300 cursor-pointer ${
                  activeTab === tab.key ? "text-neutral900" : "text-neutral700"
                }`}
                // onMouseEnter={() => {
                //   setActiveTab(tab.key);
                // }}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger
                    className="w-[120px] rounded-lg h-9"
                    aria-label="Severity"
                  >
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="all" className="rounded-lg">
                      All
                    </SelectItem>
                    <SelectItem value="critical" className="rounded-lg">
                      Critical
                    </SelectItem>
                    <SelectItem value="high" className="rounded-lg">
                      High
                    </SelectItem>
                    <SelectItem value="medium" className="rounded-lg">
                      Medium
                    </SelectItem>
                    <SelectItem value="low" className="rounded-lg">
                      Low
                    </SelectItem>
                  </SelectContent>
                </Select> */}

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              className="w-[120px] rounded-lg h-9"
              aria-label="Status"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="all" className="rounded-lg">
                All
              </SelectItem>
              <SelectItem
                value={"PENDING" || "REQUESTED"}
                className="rounded-lg"
              >
                Pending
              </SelectItem>
              {!schedule && (
                <>
                  <SelectItem value="RUNNING" className="rounded-lg">
                    Running
                  </SelectItem>
                  <SelectItem value={"STOPPED"} className="rounded-lg">
                    Stopped
                  </SelectItem>
                  <SelectItem value="DONE" className="rounded-lg">
                    Completed
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {isLoading && !isSuccess && (
          <div className="h-[55vh] w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && !isLoading && (
          <div className="h-[45vh] w-full flex justify-center flex-col items-center">
            <p className="font-semibold text-xl text-center">
              There was an error getting the data
            </p>
            <p className="text-center">{data?.message ?? ""}</p>
          </div>
        )}

        {!isLoading && isSuccess && data?.data?.data?.length > 0 && (
          <>
            <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
              {activeTab === "all" && (
                <>
                  <div className="overflow-x-auto">
                    <div className="w-full border min-w-[1100px] border-neutral300 rounded-lg text-sm">
                      <div className="flex justify-between text-left px-3 xl:px-6 py-2 bg-neutral3 rounded-t-lg border-b text-neutral700 font-medium w-full">
                        <span className="flex-1 mr-2">Scan Name</span>
                        <span className="w-[15%]">Scan Type</span>
                        <span className="w-[12%]">Status</span>
                        <span className="w-[10%]">Severity</span>
                        <span className="flex-1">Created At</span>
                        <span className="flex-1">Ended On</span>
                        <span className="w-[14%]">Action</span>
                      </div>

                      {data?.data?.data?.map((item, index) => {
                        if (
                          numericDecodedId &&
                          item.target.id !== numericDecodedId
                        ) {
                          // console.log("Item:", item.target.id, "Type:", typeof item.target.id, "Decoded Type:", typeof decodedId); // Log the item being processed with type information
                          return null; // Skip this item if the ID doesn't match
                        } else {
                          // const parts = item?.target?.target
                          //   .split(",")
                          //   .map((part) => part.trim());

                          const isDeleteModalOpen = openDeleteModal === item.id;
                          const isRunScanModalOpen =
                            openRunScanModal === item.id;
                          const isStopScanModalOpen =
                            openStopScanModal === item.id;

                          const scanTime =
                            item.reportData?.scanEndTime &&
                            item.reportData?.scanEndTime !== "-"
                              ? item.reportData.scanEndTime
                              : item.ended_at;
                          return (
                            <section
                              key={index}
                              className={`min-h-[60px] py-2 px-3 xl:px-6 flex text-neutral900 items-center ${
                                index === 0
                                  ? "border-t-0"
                                  : "border-t border-t-[#EAECF0]"
                              } hover:bg-[#F9FAFB]`}
                            >
                              <div className="flex-1 text-left truncate overflow-hidden whitespace-nowrap mr-2">
                                {item.target.name}
                              </div>

                              <div className="text-left w-[15%] ">
                                <p
                                  className={cn(
                                    item.target.is_asset_group
                                      ? "bg-neutral300"
                                      : "bg-neutral-200",
                                    "w-fit px-2 py-1 rounded-full text-xs"
                                  )}
                                >
                                  {item.target.is_asset_group
                                    ? "Asset Group"
                                    : "Custom Target"}
                                </p>
                              </div>

                              {/* <div className="flex-1 relative flex-row flex gap-2 text-left break-all mr-2">
                                <div
                                  key={index}
                                  className={cn(
                                    parts.length > 1
                                      ? "max-w-[110px]"
                                      : "max-w-[140px]",
                                    "w-fit px-2 py-1 rounded-full bg-neutral200"
                                  )}
                                >
                                  <p className="truncate">{parts[0]}</p>
                                </div>
                                {parts.length > 1 && (
                                  <div
                                    className="w-fit px-[10px] py-1 rounded-full bg-neutral200 cursor-pointer"
                                    onMouseEnter={() => setShowExtra(index)}
                                    onMouseLeave={() => setShowExtra(null)}
                                  >
                                    +{parts.length - 1}
                                    {showExtra === index && (
                                      <div className="absolute left-0 ml-0 bg-white border rounded shadow px-2 py-1 z-10">
                                        {parts.slice(1, 4).join(", ")}
                                        {parts.length > 4 && " ..."}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div> */}
                              <div className="w-[12%] text-xs">
                                {item.status === "DONE" ? (
                                  <p className="flex items-center gap-2 bg-success10 text-success100 border-[1.5px] rounded-full border-[#00C8511A] w-fit px-2 py-1 ">
                                    <FaCircleCheck className="text-[#00C851]" />
                                    Completed
                                  </p>
                                ) : (item.status === "PENDING" ||
                                    item.status === "REQUESTED") &&
                                  item.isScheduled === false ? (
                                  <p className="flex items-center gap-2 border-cream100 text-brown100 border-[1.5px] rounded-full bg-[#FFBB331A] w-fit px-2 py-1 ">
                                    <MdPending />
                                    Pending
                                  </p>
                                ) : item.isScheduled === true ? (
                                  <p className="flex items-center gap-2 border-[#d3e8f1] text-[#0BA5EC] border-[1.5px] rounded-full bg-[#F1F8FF] w-fit px-2 py-1 ">
                                    <MdPending />
                                    Scheduled
                                  </p>
                                ) : item.status === "RUNNING" ? (
                                  <RunningStatus value={item?.progress || 0} />
                                ) : item.status === "STOPPED" ? (
                                  <p className="flex items-center gap-2 bg-[#FF44441A] text-[#E12A5B] border-[1.5px] rounded-full border-[#FFC1C1] w-fit px-2 py-1 ">
                                    <IoIosCloseCircle />
                                    Stopped
                                  </p>
                                ) : (
                                  <p className="flex items-center gap-2 bg-[#FF44441A] text-[#E12A5B] border-[1.5px] rounded-full border-[#FFC1C1] w-fit px-2 py-1 ">
                                    <IoIosCloseCircle />
                                    Failed
                                  </p>
                                )}
                              </div>

                              {/* <div className="flex-1 text-left">
                            <div className="progress mb-2 w-[75%]">
                              <div
                                className="progress-bar bg-success progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style={{ width: item.progress + "%" }}
                                aria-valuenow={item.progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                {item.progress}%
                              </div>
                            </div>
                          </div> */}
                              <div className="w-[10%] text-left text-grey">
                                <div>
                                  {item?.reportData?.severityFiltered ? (
                                    <CircularScore
                                      score={item?.reportData?.severityFiltered}
                                    />
                                  ) : (
                                    <CircularScore staticScore={0} />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 text-left text-grey">
                                <span className="text-neutral800">
                                  {formatFullDate(item.created_at)},
                                </span>{" "}
                                <span className="text-neutral600">
                                  {formatTime(item.created_at)}
                                </span>
                              </div>
                              <div className="flex-1 text-left text-grey">
                                {scanTime ? (
                                  <>
                                    <span className="text-neutral800">
                                      {formatFullDate(scanTime)},
                                    </span>{" "}
                                    <span className="text-neutral600">
                                      {formatTime(scanTime)}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </div>
                              <div className="w-[14%] flex space-x-2">
                                {item.status !== "PENDING" &&
                                  item.status !== "REQUESTED" && (
                                    <>
                                      {item?.scanner_id === "web_scanner" ? (
                                        <Link
                                          to={`/dashboard/web-scan/${item?.id}/report`}
                                          className="hover:text-black"
                                          state={{
                                            report_id: item.report_id,
                                            target_name: item.target.name,
                                            status: item?.status,
                                          }}
                                        >
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                className="w-fit bg-neutral200"
                                                variant={"ghost"}
                                              >
                                                <FiPieChart size={16} />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                              <p>View report</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </Link>
                                      ) : (
                                        <Link
                                          to={`/dashboard/${item?.id}/report`}
                                          className="hover:text-black"
                                          state={{
                                            report_id: item.report_id,
                                            target_name: item.target.name,
                                            status: item?.status,
                                          }}
                                        >
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                className="w-fit bg-neutral200"
                                                variant={"ghost"}
                                              >
                                                <FiPieChart size={16} />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                              <p>View report</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </Link>
                                      )}

                                      <div>
                                        <Popover
                                          open={activeModalId === index}
                                          onOpenChange={(open) =>
                                            setActiveModalId(
                                              open ? index : null
                                            )
                                          }
                                        >
                                          <PopoverTrigger asChild>
                                            <Button
                                              className="px-2 py-[6px] rounded-lg bg-success10 text-success100 cursor-pointer"
                                              onClick={() => {
                                                setActiveModalId(index);
                                                setTargetName(
                                                  item?.target?.name
                                                );
                                              }}
                                              variant={"ghost"}
                                              disabled={
                                                item?.status === "RUNNING"
                                              }
                                            >
                                              <LuDownload size={16} />
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-fit p-1">
                                            <Button
                                              className="py-2 px-3 flex items-center gap-2 rounded-md cursor-pointer"
                                              onClick={() => {
                                                fetchPdf(item?.report_id);
                                              }}
                                              variant={"white"}
                                              loading={fetchPdfLoading}
                                            >
                                              <img
                                                src={pdf}
                                                alt="pdf"
                                                className="w-[26px]"
                                              />
                                              Export as PDF
                                            </Button>
                                            <Button
                                              className="py-2 px-3 flex items-center gap-2 rounded-md cursor-pointer"
                                              onClick={() => {
                                                downloadCsv(item?.report_id);
                                              }}
                                              variant={"white"}
                                              loading={downloadCsvLoading}
                                            >
                                              <img
                                                src={csv}
                                                alt="csv"
                                                className="w-[26px]"
                                              />
                                              Export as CSV
                                            </Button>
                                          </PopoverContent>
                                        </Popover>
                                      </div>
                                    </>
                                  )}

                                {item?.status === "DONE" ||
                                item?.status === "STOPPED" ||
                                item?.status === "FAILED" ||
                                item?.status === "INTERRUPTED" ? (
                                  <div className="flex items-center gap-2">
                                    {!viewer && (
                                      <>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div>
                                              <ScanActionsModal
                                                onOpenChange={(open) => {
                                                  open
                                                    ? setOpenRunScanModal(
                                                        item?.id
                                                      )
                                                    : setOpenRunScanModal(null);
                                                }}
                                                open={isRunScanModalOpen}
                                                data={item.target.name}
                                                id={item?.id}
                                                title="Repeat scan"
                                                text="Are you sure you want to repeat scan on"
                                                handleAction={repeatScan}
                                                loading={repeatScanPending}
                                              >
                                                <Button
                                                  className="w-fit bg-[#FFBB331A] text-brown100"
                                                  variant={"ghost"}
                                                >
                                                  <BsArrowRepeat size={16} />
                                                </Button>
                                              </ScanActionsModal>
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="bottom">
                                            <p>Repeat scan</p>
                                          </TooltipContent>
                                        </Tooltip>

                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div>
                                              <ScanActionsModal
                                                onOpenChange={(open) => {
                                                  open
                                                    ? setOpenDeleteModal(
                                                        item?.id
                                                      )
                                                    : setOpenDeleteModal(null);
                                                }}
                                                open={isDeleteModalOpen}
                                                data={item.target.name}
                                                id={item?.id}
                                                title="Delete scan"
                                                text="Are you sure you want to delete scan on"
                                                handleAction={deleteScan}
                                                loading={deleteScanPending}
                                              >
                                                <Button
                                                  className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                                                  variant={"ghost"}
                                                >
                                                  <FiTrash2 size={16} />
                                                </Button>
                                              </ScanActionsModal>
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="bottom">
                                            <p>Delete scan</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </>
                                    )}
                                  </div>
                                ) : item?.status === "RUNNING" ? (
                                  <div className="flex items-center gap-2">
                                    {!viewer && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div>
                                            <ScanActionsModal
                                              onOpenChange={(open) => {
                                                open
                                                  ? setOpenStopScanModal(
                                                      item?.id
                                                    )
                                                  : setOpenStopScanModal(null);
                                              }}
                                              open={isStopScanModalOpen}
                                              data={item.target.name}
                                              id={item?.id}
                                              title="Stop scan"
                                              text="Are you sure you want to stop scan on"
                                              handleAction={stopScan}
                                              loading={stopScanPending}
                                            >
                                              <Button
                                                className="w-fit bg-[#F973161A] text-[#E3650F]"
                                                variant={"ghost"}
                                              >
                                                <FaRegCircleStop size={16} />
                                              </Button>
                                            </ScanActionsModal>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">
                                          <p>Stop scan</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                ) : item.status === "PENDING" ||
                                  item.status === "REQUESTED" ||
                                  item?.autoStartScan === false ? (
                                  <div className="flex items-center gap-2">
                                    <Button
                                      key={item.id}
                                      className="flex items-center gap-2 w-[115px] h-[32px] outline-0 rounded-lg cursor-pointer text-neutral900 hover:text-black hover:bg-neutral300 bg-neutral200"
                                      disabled={
                                        item?.autoStartScan === true ||
                                        item?.isScheduled === true ||
                                        !viewer
                                      }
                                      onClick={() => {
                                        startScan(item?.id);
                                      }}
                                    >
                                      {isPending && scanId === item.id ? (
                                        <Spinner />
                                      ) : (
                                        <>
                                          <FiCompass />
                                          Start Scan
                                        </>
                                      )}
                                    </Button>

                                    {!viewer && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div>
                                            <ScanActionsModal
                                              onOpenChange={(open) => {
                                                open
                                                  ? setOpenDeleteModal(item?.id)
                                                  : setOpenDeleteModal(null);
                                              }}
                                              open={isDeleteModalOpen}
                                              data={item.target.name}
                                              id={item?.id}
                                              title="Delete scan"
                                              text="Are you sure you want to delete scan on"
                                              handleAction={deleteScan}
                                              loading={deleteScanPending}
                                            >
                                              <Button
                                                className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                                                variant={"ghost"}
                                              >
                                                <FiTrash2 size={16} />
                                              </Button>
                                            </ScanActionsModal>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">
                                          <p>Delete scan</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                ) : null}
                              </div>

                              {/* Modal */}
                              {/* {activeModalId === index && (
                                <div
                                  className="w-fit bg-white absolute border border-neutral200 p-1 rounded-md top-12 right-16 z-10"
                                  ref={modalRef}
                                >
                                  <Button
                                    className="py-2 px-3 flex items-center gap-2 rounded-md cursor-pointer"
                                    onClick={() => {
                                      fetchPdf(item?.report_id);
                                    }}
                                    variant={"white"}
                                    loading={fetchPdfLoading}
                                  >
                                    <img
                                      src={pdf}
                                      alt="pdf"
                                      className="w-[26px]"
                                    />
                                    Export as PDF
                                  </Button>
                                  <Button
                                    className="py-2 px-3 flex items-center gap-2 rounded-md cursor-pointer"
                                    onClick={() => {
                                      downloadCsv(item?.report_id);
                                    }}
                                    variant={"white"}
                                    loading={downloadCsvLoading}
                                  >
                                    <img
                                      src={csv}
                                      alt="csv"
                                      className="w-[26px]"
                                    />
                                    Export as CSV
                                  </Button>
                                </div>
                              )} */}
                            </section>
                          );
                        }
                      })}
                    </div>
                  </div>

                  <PaginationComponent
                    lastPage={data?.data?.last_page}
                    currentPage={currentPage}
                    handlePageChange={updatePage}
                  />
                </>
              )}

              {activeTab === "scheduled" && (
                <>
                  <div className="overflow-x-auto">
                    <div className="w-full border min-w-[1100px] border-neutral300 rounded-lg text-sm">
                      <div className="flex justify-between text-left px-3 xl:px-6 py-2 bg-neutral3 rounded-t-lg border-b text-neutral700 font-medium w-full">
                        <span className="flex-1 mr-2">Asset Name</span>
                        <span className="flex-1 mr-2">Target</span>
                        <span className="flex-1">Frequency</span>
                        <span className="flex-1">Next Scan Date</span>
                        <span className="flex-1">Created At</span>
                        <span className="w-[10%]">Action</span>
                      </div>

                      {data?.data?.data?.map((item, index) => {
                        if (
                          numericDecodedId &&
                          item.target.id !== numericDecodedId
                        ) {
                          // console.log("Item:", item.target.id, "Type:", typeof item.target.id, "Decoded Type:", typeof decodedId); // Log the item being processed with type information
                          return null; // Skip this item if the ID doesn't match
                        } else {
                          const parts = item?.target?.target
                            .split(",")
                            .map((part) => part.trim());

                          const isDeleteModalOpen = openDeleteModal === item.id;

                          return (
                            <section
                              key={index}
                              className={`min-h-[60px] py-2 px-3 xl:px-6 flex text-neutral900 items-center ${
                                index === 0
                                  ? "border-t-0"
                                  : "border-t border-t-[#EAECF0]"
                              } hover:bg-[#F9FAFB]`}
                            >
                              <div className="flex-1 text-left truncate overflow-hidden whitespace-nowrap mr-2">
                                {item.target.name}
                              </div>

                              <div className="flex-1 relative flex-row flex gap-2 text-left break-all mr-2">
                                <div
                                  key={index}
                                  className={cn(
                                    parts.length > 1
                                      ? "max-w-[110px]"
                                      : "max-w-[140px]",
                                    "w-fit px-2 py-1 rounded-full bg-neutral200"
                                  )}
                                >
                                  <p className="truncate">{parts[0]}</p>
                                </div>
                                {parts.length > 1 && (
                                  <div
                                    className="w-fit px-[10px] py-1 rounded-full bg-neutral200 cursor-pointer"
                                    onMouseEnter={() => setShowExtra(index)}
                                    onMouseLeave={() => setShowExtra(null)}
                                  >
                                    +{parts.length - 1}
                                    {showExtra === index && (
                                      <div className="absolute left-0 ml-0 bg-white border rounded shadow px-2 py-1 z-10">
                                        {parts.slice(1, 4).join(", ")}
                                        {parts.length > 4 && " ..."}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="flex-1 text-left text-grey capitalize">
                                {item?.scheduleType}
                              </div>

                              <div className="flex-1 text-left text-grey">
                                <span className="text-neutral800">
                                  {formatFullDate(item.nextDate)},
                                </span>{" "}
                                <span className="text-neutral600">
                                  {convertUtcToLocal12Hour(item.nextTime)}
                                </span>
                              </div>
                              <div className="flex-1 text-left text-grey">
                                <span className="text-neutral800">
                                  {formatFullDate(item.created_at)},
                                </span>{" "}
                                <span className="text-neutral600">
                                  {formatTime(item.created_at)}
                                </span>
                              </div>

                              <div className="w-[10%] flex space-x-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <UpdateScheduleScanModal data={item} />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom">
                                    <p>Update scan</p>
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <ScanActionsModal
                                        onOpenChange={(open) => {
                                          open
                                            ? setOpenDeleteModal(item?.id)
                                            : setOpenDeleteModal(null);
                                        }}
                                        open={isDeleteModalOpen}
                                        data={item.target.name}
                                        id={item?.id}
                                        title="Delete scan"
                                        text="Are you sure you want to delete scan on"
                                        handleAction={deleteScan}
                                        loading={deleteScanPending}
                                      >
                                        <Button
                                          className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                                          variant={"ghost"}
                                        >
                                          <FiTrash2 size={16} />
                                        </Button>
                                      </ScanActionsModal>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom">
                                    <p>Delete scan</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </section>
                          );
                        }
                      })}
                    </div>
                  </div>

                  <PaginationComponent
                    lastPage={data?.data?.last_page}
                    currentPage={currentPage}
                    handlePageChange={updatePage}
                  />
                </>
              )}
            </div>
          </>
        )}

        {data?.data?.data?.length === 0 && !isLoading && isSuccess && (
          <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
            <img src={empty} alt="empty" />
            <p className=" font-semibold mt-4 text-darkGrey">No scans yet</p>
            <p className="text-neutral600 text-sm max-w-[360px] my-3">
              Initiate scans on your configured assets or browse historical scan
              results to understand the state of your web properties.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ScanHistory;
